export interface AnalyticsDeliveryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface AnalyticsDeliveryEnvelope {
  id: string;
  body: string;
  attempts: number;
  createdAt: number;
  nextAttemptAt: number;
}

export interface ReliableAnalyticsDeliveryOptions {
  collectorUrl: string;
  storageKey: string;
  fetchImpl?: typeof fetch;
  storage?: AnalyticsDeliveryStorage | null;
  now?: () => number;
  schedule?: (callback: () => void, delayMs: number) => unknown;
  maxQueueSize?: number;
}

const memoryQueues = new Map<string, AnalyticsDeliveryEnvelope[]>();

const browserStorage = (): AnalyticsDeliveryStorage | null => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};

const defaultSchedule = (callback: () => void, delayMs: number): unknown =>
  typeof window !== "undefined"
    ? window.setTimeout(callback, delayMs)
    : setTimeout(callback, delayMs);

const parseQueue = (
  raw: string | null,
  fallback: AnalyticsDeliveryEnvelope[],
): AnalyticsDeliveryEnvelope[] => {
  if (!raw) return fallback;
  try {
    const value = JSON.parse(raw);
    if (!Array.isArray(value)) return fallback;
    return value.filter(item =>
      item
      && typeof item.id === "string"
      && typeof item.body === "string"
      && Number.isSafeInteger(item.attempts)
      && Number.isFinite(item.createdAt)
      && Number.isFinite(item.nextAttemptAt),
    );
  } catch {
    return fallback;
  }
};

const retryDelayMs = (attempts: number): number =>
  Math.min(60_000, 1_000 * 2 ** Math.min(attempts, 6));

const shouldRemove = (status: number): boolean =>
  status >= 200 && status < 300
  || (status >= 400 && status < 500 && status !== 408 && status !== 429);

export const createReliableAnalyticsDelivery = ({
  collectorUrl,
  storageKey,
  fetchImpl = fetch,
  storage = browserStorage(),
  now = Date.now,
  schedule = defaultSchedule,
  maxQueueSize = 100,
}: ReliableAnalyticsDeliveryOptions) => {
  let flushPromise: Promise<void> | null = null;
  let retryScheduled = false;

  const load = (): AnalyticsDeliveryEnvelope[] => {
    const fallback = memoryQueues.get(storageKey) ?? [];
    try {
      const queue = parseQueue(storage?.getItem(storageKey) ?? null, fallback);
      memoryQueues.set(storageKey, queue);
      return queue;
    } catch {
      return fallback;
    }
  };

  const save = (queue: AnalyticsDeliveryEnvelope[]): void => {
    memoryQueues.set(storageKey, queue);
    try {
      storage?.setItem(storageKey, JSON.stringify(queue));
    } catch {
      // The in-memory queue still protects the current tab.
    }
  };

  const scheduleRetry = (queue: AnalyticsDeliveryEnvelope[]): void => {
    if (retryScheduled || queue.length === 0) return;
    retryScheduled = true;
    const delay = Math.max(
      250,
      Math.min(...queue.map(item => item.nextAttemptAt)) - now(),
    );
    schedule(() => {
      retryScheduled = false;
      void flush();
    }, delay);
  };

  const flush = (): Promise<void> => {
    if (flushPromise) return flushPromise;
    flushPromise = (async () => {
      let queue = load();
      for (const envelope of [...queue]) {
        if (envelope.nextAttemptAt > now()) continue;
        let delivered = false;
        let terminal = false;
        for (let immediateAttempt = 0; immediateAttempt < 2; immediateAttempt += 1) {
          try {
            const response = await fetchImpl(collectorUrl, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: envelope.body,
              keepalive: true,
            });
            terminal = shouldRemove(response.status);
            delivered = response.ok;
            if (terminal || delivered) break;
          } catch {
            // The exact serialized envelope is retried once before backoff.
          }
        }
        if (terminal || delivered) {
          queue = queue.filter(item => item.id !== envelope.id);
        } else {
          queue = queue.map(item => item.id === envelope.id
            ? {
                ...item,
                attempts: item.attempts + 1,
                nextAttemptAt: now() + retryDelayMs(item.attempts + 1),
              }
            : item);
        }
        save(queue);
      }
      scheduleRetry(queue);
    })().finally(() => {
      flushPromise = null;
    });
    return flushPromise;
  };

  const enqueue = (id: string, payload: unknown): Promise<void> => {
    const body = JSON.stringify(payload);
    const existing = load();
    if (!existing.some(item => item.id === id)) {
      const queue = [
        ...existing,
        { id, body, attempts: 0, createdAt: now(), nextAttemptAt: now() },
      ].slice(-maxQueueSize);
      save(queue);
    }
    return flush();
  };

  if (typeof window !== "undefined") {
    window.addEventListener("online", () => {
      void flush();
    });
  }

  queueMicrotask(() => {
    void flush();
  });

  return {
    enqueue,
    flush,
    pendingCount: (): number => load().length,
  };
};
